import { Link } from '@mui/material'
import {
  getGridDateOperators,
  getGridStringOperators,
  GridFilterInputValue,
  GridValueGetterParams
} from '@mui/x-data-grid'
import { IDataGridColumn } from 'components/DataGrid/DataGrid'
import { startCase } from 'lodash-es'
import { numberToCurrency } from 'utility/numberToCurrency'
import { AddressState, IAddress, IAllAmazonOrdersQuery, ShippingAmazonOrderStatus } from 'types'

export type FilterStartingWith<Set, Needle extends string> = Set extends `${Needle}${infer _X}`
  ? Set
  : never

export const dateFilterOperators = getGridDateOperators().filter(
  (operator) => operator.value !== 'not'
)

type AmazonOrder = IAllAmazonOrdersQuery['allAmazonOrders']['orders'][0]

export const columns: (IDataGridColumn & { position?: number })[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 100,
    type: 'number',
    valueFormatter: (params) => params.value
  },
  {
    field: 'amazonOrderId',
    headerName: 'Amazon Order ID',
    width: 180,
    sortable: false
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    type: 'singleSelect',
    valueOptions: Object.values(ShippingAmazonOrderStatus),
    valueFormatter: (params) => startCase(params.value)
  },
  {
    field: 'total',
    headerName: 'Total',
    width: 125,
    type: 'number',
    valueFormatter: (params) => numberToCurrency(params.value)
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    width: 200
  },
  {
    field: 'purchasedAt',
    type: 'dateTime',
    headerName: 'Purchased At',
    width: 160,
    filterOperators: dateFilterOperators
  },
  {
    field: 'items.asin',
    headerName: 'ASIN',
    width: 160,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) =>
      params.row.items.map((item) => item.asin).join(', ')
  },
  {
    field: 'items.sku',
    headerName: 'SKU',
    width: 200,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) =>
      params.row.items.map((item) => item.sku).join(', ')
  },
  {
    field: 'items.amazonOrderItemId',
    headerName: 'Amazon Order Item ID',
    width: 160,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) =>
      params.row.items.map((item) => item.amazonOrderItemId).join(', ')
  },
  {
    field: 'items.quantity',
    headerName: 'Quantity',
    type: 'number',
    width: 160,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) =>
      params.row.items.reduce((sum, item) => sum + item.quantity, 0)
  },
  {
    field: 'shippingOrder.id',
    headerName: 'Shipping Order ID',
    width: 160,
    renderCell: (params) => <Link href={`/orders/${params.value}/shipments`}>{params.value}</Link>,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.shippingOrder?.id
  },
  {
    field: 'buyer.emailAlias',
    headerName: 'Email Alias',
    minWidth: 320,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.buyer?.emailAlias
  },
  {
    field: 'buyer.userId',
    headerName: 'User ID',
    minWidth: 100,
    type: 'number',
    renderCell: (params) => <Link href={`/users/${params.value}`}>{params.value}</Link>,
    valueFormatter: (params) => params.value,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.buyer?.userId
  },
  {
    field: 'address.name',
    headerName: 'Address Full Name',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.name,
    width: 200
  },
  {
    field: 'address.street',
    headerName: 'Street',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.street,
    width: 300
  },
  {
    field: 'address.street2',
    headerName: 'Street 2',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.street2,
    width: 200
  },
  {
    field: 'address.city',
    headerName: 'City',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.city,
    width: 140
  },
  {
    field: 'address.zipCode',
    headerName: 'Zip Code',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.zipCode,
    width: 100
  },
  {
    field: 'address.state',
    type: 'singleSelect',
    valueOptions: Object.values(AddressState).sort(),
    headerName: 'State',
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.state,
    width: 80
  },
  {
    field: 'address.country',
    headerName: 'Country',
    width: 100,
    valueGetter: (params: GridValueGetterParams<any, AmazonOrder>) => params.row.address?.country,
    filterOperators: [
      ...getGridStringOperators(),
      {
        value: 'not',
        getApplyFilterFn: () => null,
        InputComponent: GridFilterInputValue
      }
    ]
  },
  {
    field: 'generated.address.fullShippingAddress',
    headerName: 'Full Shipping Address',
    width: 440,
    sortable: false,
    filterable: false,
    valueGetter: (params) => {
      const address: IAddress | null = params.row.address

      if (!address) {
        return ''
      }

      const fullAddress = (address.street + ' ' + (address.street2 || '')).trim()

      return `${fullAddress}, ${address.city}, ${address.state}, ${address.zipCode}, ${address.country}`
    }
  }
]
